import { useEffect, useState } from "react";
import { RefreshCw, Loader2, TrendingUp, Trophy, X } from "lucide-react";
import { api } from "@/lib/api";

const GRADE_STYLE = {
  A: "bg-emerald-100 text-emerald-800 border-emerald-300",
  B: "bg-sky-100 text-sky-800 border-sky-300",
  C: "bg-amber-100 text-amber-800 border-amber-300",
  D: "bg-orange-100 text-orange-800 border-orange-300",
  E: "bg-rose-100 text-rose-800 border-rose-300",
};

function GradeBadge({ grade, size = "md" }) {
  const letter = String(grade || "?").charAt(0).toUpperCase();
  const cls = GRADE_STYLE[letter] || "bg-stone-100 text-stone-800 border-stone-300";
  const sz = size === "lg"
    ? "w-14 h-14 text-2xl"
    : size === "sm"
    ? "w-6 h-6 text-[11px]"
    : "w-8 h-8 text-sm";
  return (
    <div className={`inline-flex items-center justify-center rounded-lg border font-bold ${sz} ${cls}`}>
      {grade}
    </div>
  );
}

/** Honest, live-computed coverage self-assessment. Renders at the top
 *  of the Progress Dash so operators see the summary before diving into
 *  metric tiles below. Grades regenerate on every refresh; peer grades
 *  are curated (my expert judgement, not auto-updated). */
export default function CoverageAssessmentCard() {
  const [data, setData]     = useState(null);
  const [err,  setErr]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const [r, h] = await Promise.all([
        api.get("/admin/progress/coverage-assessment"),
        api.get("/admin/progress/assessment-history?days=30").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setHistory(h.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <div data-testid="coverage-assessment-loading" className="border border-stone-200 rounded-xl bg-white p-5 mb-6 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
        <div className="text-sm text-stone-500">Computing coverage assessment…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="border border-rose-200 rounded-xl bg-rose-50 p-5 mb-6 text-sm text-rose-800">
        {err}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      data-testid="tile-coverage-assessment"
      className="border-2 border-stone-900 rounded-xl bg-gradient-to-br from-stone-50 to-white p-6 mb-6 shadow-[0_2px_0_rgba(0,0,0,0.04)]"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-start gap-4">
          <GradeBadge grade={data.overall_grade} size="lg" />
          <div>
            <div className="text-xs font-underground-sc tracking-widest text-stone-500 mb-1">
              Global coverage self-assessment
            </div>
            <div className="text-lg font-semibold text-stone-900 leading-tight">
              Overall grade <span data-testid="overall-grade-letter">{data.overall_grade}</span>
              <span className="text-stone-400 text-sm font-normal ml-2">
                weighted {data.weighted_score.toFixed(2)} / 4.00
              </span>
            </div>
            <div className="text-xs text-stone-500 mt-1">
              {data.totals?.degrees?.toLocaleString()} degrees ·{" "}
              {data.totals?.universities?.toLocaleString()} universities ·{" "}
              {data.totals?.countries} countries indexed
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <HistorySparkline history={history?.history} />
          <button
            type="button"
            onClick={refresh}
            data-testid="coverage-assessment-refresh"
            className="text-stone-500 hover:text-stone-800"
            title="Recompute"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* ── Dimension table (WUA vs peer) ────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-5">
        {data.dimensions.map(d => (
          <div
            key={d.key}
            data-testid={`dim-${d.key}`}
            className="flex items-center gap-3 py-1.5 border-b border-stone-100 last:border-0"
          >
            <GradeBadge grade={d.wua_grade} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-stone-800 truncate flex items-center gap-1.5">
                {d.name}
                {d.differentiator && <Trophy className="w-3 h-3 text-amber-500" title="Differentiator vs peers" />}
              </div>
              <div className="text-[11px] text-stone-500 truncate">{d.wua_note}</div>
              {d.key === "entry_reqs" && data.uni_entry_req && (
                <div
                  data-testid="uni-entry-req-context"
                  className="mt-1 flex items-center gap-1.5 text-[11px] text-stone-400"
                  title={data.uni_entry_req.note}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-300" />
                  <span>
                    Uni-wide range (context only):{" "}
                    <span className="text-stone-600 font-mono">
                      {data.uni_entry_req.uk_pct}%
                    </span>{" "}
                    of UK unis ({data.uni_entry_req.uk_typical_populated}/
                    {data.uni_entry_req.uk_universe})
                  </span>
                </div>
              )}
            </div>
            <div className="flex-none text-right">
              <div className="text-[10px] uppercase tracking-wider text-stone-400">vs {d.peer_name}</div>
              <div className="flex items-center justify-end gap-1 mt-0.5">
                <GradeBadge grade={d.peer_grade} size="sm" />
                <span className="text-[11px] text-stone-500 font-mono">{d.wua_pct_display}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── US breadth-sources stacked bar ──────────────────────── */}
      {data.breadth_sources?.total > 0 && (
        <BreadthSourcesTile sources={data.breadth_sources} />
      )}

      {/* ── Conclusion + Recommendation panels ──────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-stone-200 rounded-lg bg-white p-4">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Conclusion — go live?
          </div>
          <div
            data-testid="conclusion-decision"
            className={`text-sm font-semibold mb-1 ${
              data.go_live?.decision?.startsWith("Ship") ? "text-emerald-800"
              : data.go_live?.decision?.startsWith("Yes") ? "text-sky-800"
              : data.go_live?.decision?.startsWith("Beta") ? "text-amber-800"
              : "text-rose-800"
            }`}
          >
            {data.go_live?.decision}
          </div>
          <div className="text-[12px] text-stone-700 leading-relaxed">
            {data.go_live?.why}
          </div>
        </div>

        <div className="border border-stone-200 rounded-lg bg-white p-4">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Recommendation — biggest gain
          </div>
          <div data-testid="recommendation-priority" className="text-[11px] font-mono text-stone-500 mb-1">
            {data.recommendation?.priority}
          </div>
          <div className="text-[12px] text-stone-800 leading-relaxed mb-2">
            {data.recommendation?.action}
          </div>
          {data.recommendation?.expected_grade_lift && (
            <div className="text-[11px] text-emerald-700 italic">
              {data.recommendation.expected_grade_lift}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 text-[10px] text-stone-400">
        WUA grades recompute from live Mongo. Peer grades are curated. {data.rubric_note}
      </div>
    </div>
  );
}

/** 30-day sparkline of entry-req % and narrated %. Empty state when
 *  no history has been logged yet. Very small, sits in the header. */
function HistorySparkline({ history }) {
  if (!history || history.length < 2) return null;
  const W = 120, H = 34;
  const er   = history.map(r => r.entry_req_pct || 0);
  const narr = history.map(r => r.narrated_pct || 0);
  const mx   = Math.max(1, ...er, ...narr);
  const step = W / Math.max(1, history.length - 1);
  const path = arr => arr.map((v, i) =>
    `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(H - (v / mx) * (H - 4) - 2).toFixed(1)}`
  ).join(" ");
  const lastER = er[er.length - 1], firstER = er[0];
  const delta = lastER - firstER;
  return (
    <div data-testid="assessment-history-sparkline" className="text-right">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <line x1="0" y1={H - 1} x2={W} y2={H - 1} stroke="currentColor" strokeOpacity="0.08" />
        <path d={path(er)}   fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={path(narr)} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="text-[10px] text-stone-500 mt-0.5 flex items-center justify-end gap-2">
        <span><span className="inline-block w-2 h-0.5 bg-sky-500 align-middle mr-1"></span>entry-req</span>
        <span><span className="inline-block w-2 h-0.5 bg-violet-500 align-middle mr-1"></span>narrated</span>
        <span className={`font-mono ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-stone-500"}`}>
          {delta > 0 ? "▲" : delta < 0 ? "▼" : "="}
          {Math.abs(delta).toFixed(1)}%
        </span>
      </div>
    </div>
  );
}


/** Stacked-bar showing how the US degree catalogue is composed —
 *  editorial / IPEDS CIP-4 / IPEDS CIP-6 variants / Scorecard /
 *  other. Attributes the 97% breadth grade to its sources so the
 *  operator can see WHERE depth is coming from and which buckets
 *  are moving. */
const _BUCKET_META = {
  editorial:     { label: "Editorial",      color: "bg-emerald-500", text: "text-emerald-700" },
  ipeds_cip6:    { label: "IPEDS CIP-6",    color: "bg-sky-500",     text: "text-sky-700"     },
  ipeds_cip4:    { label: "IPEDS CIP-4",    color: "bg-indigo-500",  text: "text-indigo-700"  },
  scorecard:     { label: "Scorecard",      color: "bg-violet-500",  text: "text-violet-700"  },
  authoritative: { label: "Authoritative",  color: "bg-sky-500",     text: "text-sky-700"     },
  scrape:        { label: "Scrape",         color: "bg-violet-500",  text: "text-violet-700"  },
  other:         { label: "Other",          color: "bg-stone-400",   text: "text-stone-600"   },
};
// Ordered so bars read left-to-right from most-curated to least.
const _BUCKET_ORDER = [
  "editorial", "ipeds_cip4", "ipeds_cip6", "scorecard",
  "authoritative", "scrape", "other",
];

/** Single-country row — stacked bar + legend. */
function BreadthSourcesRow({ entry, highlight = false }) {
  const total = entry.total || 0;
  if (total === 0) return null;
  const buckets = _BUCKET_ORDER
    .map(k => ({ k, meta: _BUCKET_META[k], n: entry.buckets?.[k] || 0 }))
    .filter(b => b.n > 0);
  return (
    <div
      data-testid={`breadth-sources-row-${entry.country}`}
      className="py-2 border-b border-stone-100 last:border-b-0"
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-baseline gap-2">
          <span
            className={`text-[11px] font-semibold tracking-wide ${
              highlight ? "text-emerald-700" : "text-stone-700"
            }`}
          >
            {entry.country}
          </span>
          <span className="text-[11px] text-stone-500 font-mono">
            {total.toLocaleString()} rows
          </span>
        </div>
        <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5">
          {buckets.map(({ k, meta, n }) => {
            const pct = (n / total) * 100;
            return (
              <span
                key={k}
                className="inline-flex items-center gap-1 text-[10px]"
                data-testid={`breadth-src-legend-${entry.country}-${k}`}
              >
                <span className={`inline-block w-2 h-2 rounded-sm ${meta.color}`} />
                <span className={meta.text}>{meta.label}</span>
                <span className="text-stone-400 font-mono">{pct.toFixed(1)}%</span>
              </span>
            );
          })}
        </div>
      </div>
      <div
        className="flex rounded-md overflow-hidden h-3 border border-stone-200"
        data-testid={`breadth-sources-bar-${entry.country}`}
      >
        {buckets.map(({ k, meta, n }) => {
          const pct = (n / total) * 100;
          return (
            <div
              key={k}
              className={`${meta.color} h-full`}
              style={{ width: `${pct}%` }}
              title={`${meta.label}: ${n.toLocaleString()} rows (${pct.toFixed(1)}%)`}
              data-testid={`breadth-src-seg-${entry.country}-${k}`}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Multi-country breadth sources tile. */
function BreadthSourcesTile({ sources }) {
  const list = sources.countries || [];
  if (!list.length) return null;
  return (
    <div
      data-testid="breadth-sources-tile"
      className="border border-stone-200 rounded-lg bg-white p-4 mb-5"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
          Breadth sources by country
        </div>
        <div className="text-[10px] text-stone-400">
          hover a segment for exact counts
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {list.map((entry) => (
          <BreadthSourcesRow
            key={entry.country}
            entry={entry}
            highlight={entry.country === "US"}
          />
        ))}
      </div>
    </div>
  );
}
